import { useEffect, useState, useCallback, useRef } from "react";
import { Thermometer, AlertTriangle, TrendingUp, TrendingDown, ActivitySquare, Download } from "lucide-react";
import { toPng } from "html-to-image";
import { api, todayISO } from "../lib/api";
import { toast } from "sonner";
import FridgeChart from "../components/FridgeChart";

function daysAgoISO(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}

function FridgeCard({ f }) {
    const ref = useRef(null);
    const [downloading, setDownloading] = useState(false);

    async function downloadImage() {
        if (!ref.current) return;
        setDownloading(true);
        try {
            const url = await toPng(ref.current, {
                backgroundColor: "#FFFFFF",
                pixelRatio: 2,
                cacheBust: true,
            });
            const link = document.createElement("a");
            link.href = url;
            link.download = `temperature_${(f.frigo.nome || "frigo").replace(/[^a-z0-9]+/gi, "_")}_${todayISO()}.png`;
            link.rel = "noopener";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success("Grafico scaricato");
        } catch (e) {
            toast.error("Errore download grafico");
        } finally {
            setDownloading(false);
        }
    }

    return (
        <section className="card-soft p-6" data-testid={`frigo-card-${f.frigo.id}`}>
            <div ref={ref} className="bg-white" data-testid={`frigo-card-export-${f.frigo.id}`}>
                <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                    <div className="flex items-start gap-3">
                        <div className="w-11 h-11 rounded-xl bg-forest text-white grid place-items-center shrink-0">
                            <Thermometer size={20} />
                        </div>
                        <div>
                            <h2 className="font-display text-xl font-semibold text-forest-dark">
                                {f.frigo.nome}
                            </h2>
                            <p className="text-xs text-stone-500">
                                Range conformità: {f.frigo.min_c}°C — {f.frigo.max_c}°C
                                {f.frigo.buffer_c > 0 && <> · margine warning ±{f.frigo.buffer_c}°C</>}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {f.breaches > 0 && (
                            <div className="chip bg-red-50 text-red-700 border border-red-200">
                                <AlertTriangle size={12} /> {f.breaches} fuori soglia
                            </div>
                        )}
                        {f.warnings > 0 && (
                            <div className="chip bg-amber-50 text-amber-800 border border-amber-200">
                                <AlertTriangle size={12} /> {f.warnings} warning
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 stagger">
                    <div className="rounded-xl bg-bone-2 p-3">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Letture</p>
                        <p className="font-display text-xl font-semibold text-forest-dark mt-0.5">{f.count}</p>
                    </div>
                    <div className="rounded-xl bg-bone-2 p-3">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Media</p>
                        <p className="font-display text-xl font-semibold text-forest-dark mt-0.5">{f.avg_val != null ? `${f.avg_val}°` : "—"}</p>
                    </div>
                    <div className="rounded-xl bg-bone-2 p-3">
                        <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-500">
                            <TrendingDown size={10} /> Min
                        </div>
                        <p className="font-display text-xl font-semibold text-forest-dark mt-0.5">{f.min_val != null ? `${f.min_val}°` : "—"}</p>
                    </div>
                    <div className="rounded-xl bg-bone-2 p-3">
                        <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-500">
                            <TrendingUp size={10} /> Max
                        </div>
                        <p className="font-display text-xl font-semibold text-forest-dark mt-0.5">{f.max_val != null ? `${f.max_val}°` : "—"}</p>
                    </div>
                </div>

                <FridgeChart frigo={f.frigo} readings={f.readings} />
            </div>

            <div className="mt-4 flex flex-wrap gap-2 justify-between items-center">
                <button
                    onClick={downloadImage}
                    disabled={downloading || !f.readings.length}
                    className="btn-ghost text-xs inline-flex items-center gap-2"
                    data-testid={`download-chart-${f.frigo.id}`}
                >
                    <Download size={14} />
                    {downloading ? "Generando..." : "Scarica grafico (PNG)"}
                </button>
                {f.readings.length > 0 && (
                    <span className="text-xs text-stone-500">Utile da allegare al report ASL</span>
                )}
            </div>

            {f.readings.length > 0 && (
                <details className="mt-5" data-testid={`details-${f.frigo.id}`}>
                    <summary className="cursor-pointer text-sm text-forest font-semibold">
                        Mostra tutte le letture ({f.readings.length})
                    </summary>
                    <div className="mt-3 max-h-64 overflow-y-auto border border-soft rounded-xl">
                        <table className="w-full text-xs">
                            <thead className="bg-bone-2 text-stone-500 sticky top-0">
                                <tr>
                                    <th className="text-left p-2">Data</th>
                                    <th className="text-left p-2">Slot</th>
                                    <th className="text-right p-2">Valore</th>
                                    <th className="text-left p-2">Stato</th>
                                    <th className="text-left p-2">Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...f.readings].reverse().map((r, idx) => (
                                    <tr key={idx} className={`border-t border-soft ${r.out_of_range ? "bg-red-50" : r.warning ? "bg-amber-50" : ""}`}>
                                        <td className="p-2">{r.data}</td>
                                        <td className="p-2 capitalize">{r.slot}</td>
                                        <td className={`p-2 text-right font-semibold ${r.out_of_range ? "text-red-700" : r.warning ? "text-amber-800" : "text-forest"}`}>
                                            {r.valore}°C
                                        </td>
                                        <td className="p-2 text-xs">
                                            {r.out_of_range ? "⚠ Fuori soglia" : r.warning ? "⚠ Vicino al limite" : "✓ Conforme"}
                                        </td>
                                        <td className="p-2 text-stone-500">{r.note || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
            )}
        </section>
    );
}

export default function Temperature() {
    const [start, setStart] = useState(daysAgoISO(30));
    const [end, setEnd] = useState(todayISO());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/haccp/temperatures", { params: { start, end } });
            setData(r.data);
        } catch {
            toast.error("Errore caricamento storico");
        } finally {
            setLoading(false);
        }
    }, [start, end]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="space-y-6 pb-6" data-testid="temperature-page">
            <section className="card-soft p-5 flex flex-wrap items-end gap-4 justify-between">
                <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-forest-500">Periodo</p>
                    <div className="flex gap-2 mt-2">
                        <input
                            type="date"
                            value={start}
                            onChange={(e) => setStart(e.target.value)}
                            className="input-soft max-w-[160px]"
                            data-testid="temp-start"
                        />
                        <input
                            type="date"
                            value={end}
                            onChange={(e) => setEnd(e.target.value)}
                            className="input-soft max-w-[160px]"
                            data-testid="temp-end"
                        />
                    </div>
                </div>
                <div className="flex gap-2 text-xs">
                    <button onClick={() => { setStart(daysAgoISO(7)); setEnd(todayISO()); }} className="btn-ghost text-xs" data-testid="preset-7">7 giorni</button>
                    <button onClick={() => { setStart(daysAgoISO(30)); setEnd(todayISO()); }} className="btn-ghost text-xs" data-testid="preset-30">30 giorni</button>
                    <button onClick={() => { setStart(daysAgoISO(90)); setEnd(todayISO()); }} className="btn-ghost text-xs" data-testid="preset-90">90 giorni</button>
                </div>
            </section>

            {loading ? (
                <div className="card-soft p-6 h-64 animate-pulse" />
            ) : (
                data?.frigoriferi?.map((f) => <FridgeCard key={f.frigo.id} f={f} />)
            )}

            {data?.frigoriferi?.length === 0 && (
                <section className="card-soft p-10 text-center text-stone-500">
                    <ActivitySquare size={36} className="mx-auto opacity-40" />
                    <p className="mt-3 text-sm">
                        Nessun frigorifero configurato. Vai in <strong>Impostazioni</strong> per aggiungerne uno.
                    </p>
                </section>
            )}
        </div>
    );
}
